import React from 'react';
import { Chip, Link, Avatar, Snippet } from '@heroui/react';
import { Icon } from '@iconify/react';
import { SubscriberData } from '../types';
import { LinkIcon } from '../../../../components/icons';
import { Typography } from '../../../../components';
import { ResponsiveTooltip } from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';

interface SubscriberCampaignCellProps {
  subscriber: SubscriberData;
}

const SubscriberCampaignCell: React.FC<SubscriberCampaignCellProps> = ({ subscriber }) => {
  const { t } = useTranslations();

  const getCampaignNameFromLink = (link?: string) => {
    if (!link) return subscriber.company.source || t('subscribers.campaign.unknown');

    try {
      const url = new URL(link);
      const cleanedPath = url.pathname.replace(/\/+$/, '');
      const segments = cleanedPath.split('/').filter(Boolean);
      if (segments.length) return segments[segments.length - 1];
      return url.hostname;
    } catch {
      const cleanedLink = link.replace(/\/+$/, '');
      const parts = cleanedLink.split('/').filter(Boolean);
      return parts.length ? parts[parts.length - 1] : link;
    }
  };

  const formatCampaignDate = (date?: string) => {
    if (!date) return '';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString('ru-RU');
  };

  const campaignName = getCampaignNameFromLink(subscriber.company.link);
  const campaignDate = formatCampaignDate(subscriber.company.createdAt);
  const chipLabel = [campaignName.slice(-4), campaignDate].filter(Boolean).join(' ');
  const clicksCount = subscriber.company?.clicks ?? 0;
  const subscribersPercent = subscriber.company?.subscribersPercent;
  const formattedSubscribersPercent =
    typeof subscribersPercent === 'number' && !Number.isNaN(subscribersPercent)
      ? subscribersPercent.toLocaleString('ru-RU', { maximumFractionDigits: 1 })
      : null;
  const subscribersPercentLabel = formattedSubscribersPercent ?? '0';

  return (
    <div className="flex flex-col gap-1">
      {/* Первая строка: Имя пользователя */}
      <div className="flex items-center gap-1">
        <Avatar
          src={subscriber?.avatar}
          showFallback
          name={subscriber.name?.slice(0, 1)}
          className="w-5 h-5 rounded-full"
        />
        <span className="text-sm font-medium text-default-900">{subscriber.name}</span>
      </div>

      {/* Вторая строка: Аватар, ID и ссылка на аккаунт */}
      <div className="flex items-center gap-2">
        <Avatar
          src={subscriber.model.avatar}
          name={subscriber.model.name}
          showFallback
          size="sm"
          className="w-5 h-5 rounded-1"
        />
        <span className="text-sm font-medium text-default-900">{subscriber.model.name}</span>
        <ResponsiveTooltip content={t('subscribers.campaign.nicknameTooltip')} placement="top">
          <Link
            href={`https://onlyfans.com/${subscriber.model.ofUserName}`}
            className="text-sm text-partner flex items-center gap-1"
            isExternal
          >
            @{subscriber.model.ofUserName}
          </Link>
        </ResponsiveTooltip>
      </div>

      {/* Третья строка: Чип кампании и информация */}
      <div className="flex items-center gap-2">
        <Chip
          size="sm"
          color="secondary"
          variant="flat"
          className="!text-xs !text-accent"
          classNames={{
            content: '!text-xs !text-accent',
          }}
        >
          {chipLabel}
        </Chip>
        <ResponsiveTooltip content={t('subscribers.campaign.byLinkTooltip')} placement="top">
          <div>
            <Typography variant="label3" className="md:font-semibold">
              {subscriber.company.link?.toLowerCase().includes('trial') ? 'Free-Trial' : t('subscribers.campaign.byLink')}
            </Typography>
          </div>
        </ResponsiveTooltip>
      </div>
      <div className="flex items-center gap-1">
        <Snippet
          symbol={null}
          codeString={subscriber.company.link}
          className="bg-husky rounded-full px-2 py-[0.1875rem]"
          classNames={{
            base: 'bg-husky rounded-full px-2 py-[0.1875rem] flex items-center gap-1 shadow-none',
            pre: 'bg-transparent p-0 flex items-center gap-1 max-w-[13.75rem]',
            copyButton: 'text-partner min-w-5 h-5',
            checkIcon: 'text-partner',
            content: 'flex items-center gap-1 max-w-[13.75rem]',
          }}
        >
          <a
            href={subscriber.company.link}
            target="__blank"
            className="flex items-center gap-1 no-underline"
          >
            <LinkIcon size={18} fill="#006FEE" className="text-partner" />
            <Typography variant="body2" className="!text-partner truncate max-w-[11.25rem]">
              {subscriber.company.link}
            </Typography>
          </a>
        </Snippet>
        <div className="flex items-center gap-1">
          <ResponsiveTooltip content={t('subscribers.campaign.guestsTooltip')} placement="top">
            <div className="flex items-center gap-1 cursor-pointer">
              <Icon icon="solar:eye-bold" width={16} height={16} className="text-default-400" />
              <span className="text-xs text-default-500">{clicksCount}</span>
            </div>
          </ResponsiveTooltip>
          <ResponsiveTooltip content={t('subscribers.campaign.conversionTooltip')} placement="top">
            <span className="text-xs text-default-600 cursor-pointer">({subscribersPercentLabel}%)</span>
          </ResponsiveTooltip>
        </div>
      </div>

      {/* Четвертая строка: Ссылка и статистика */}
      {/* <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
        <Icon icon="solar:link-linear" width={16} height={16} className="text-primary" />
        <Link 
          href={subscriber.company.link} 
          className="text-sm text-primary truncate flex-1"
          isExternal
        >
          {subscriber.company.link}
        </Link>
        <div className="flex items-center gap-1 ml-auto">
          <Icon icon="solar:eye-bold" width={16} height={16} className="text-default-400" />
          <span className="text-xs text-default-500">{subscriber.transactionsCount}</span>
          <span className="text-xs text-default-600">(67%)</span>
        </div>
      </div> */}
    </div>
  );
};

export default SubscriberCampaignCell;
